import {Component, inject, OnInit, PLATFORM_ID, signal, WritableSignal} from '@angular/core';
import {ButtonDirective} from "primeng/button";
import {CardModule} from "primeng/card";
import {CardNoticias, StateRequest} from "../../../../shared/interfaces/sede_electronica";
import {DatePipe, isPlatformBrowser} from "@angular/common";
import {NoticiasRssService} from "../../../../services/sede-electronica/noticias-rss.service";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {firstValueFrom, forkJoin} from "rxjs";
import {TooltipModule} from 'primeng/tooltip';
import {SkeletonModule} from "primeng/skeleton";
import {ProgressSpinnerModule} from "primeng/progressspinner";

@Component({
  selector: 'app-sede-electronica-noticias',
  standalone: true,
  imports: [
    ButtonDirective,
    CardModule,
    DatePipe,
    TranslatePipe,
    SkeletonModule,
    ProgressSpinnerModule,
    TooltipModule
  ],
  template: `
    <section class="width-container">
      <div class="flex align-items-center justify-content-between mb-4">
        <h1
          class="font-24 title-blue font-weight-regular text-left"> {{ 'components.app-sede-electronica-noticias.titulo' | translate }}</h1>
        <button pButton class="p-button-rounded
             hidden sm:block border-outline-blue-light
      bg-button-outline-blue-light text-blue-general" (click)="verMasNoticias(false)">
          <span>{{ 'components.app-sede-electronica-noticias.ver-mas-noticias' | translate }}</span>
          <i class="pi pi-angle-right ml-2"></i>
        </button>
      </div>
      <section class="noticias-main-content">
        @if (stateRequest.complete) {
          @for (itemCard of cards; track $index) {
            <p-card class="noticias-card">
              <p class="text-gray font-principal mt-1 font-16 mb-2">{{ itemCard.pubDate | date : 'yyyy-MM-dd' }}</p>
              <p class="text-orange-500 font-principal font-16 my-2 hidden sm:block">{{ itemCard.categories }}</p>
              <h1 (click)="verMasNoticias(false, itemCard.link)" [class.cursor-pointer]="itemCard.link"
                  class="title-noticia-card font-16 font-principal ml-0 mt-3">{{ itemCard.title }}</h1>

              <p class="text-gray font-13 mb-0 noticia-descripcion font-principal"
                 [pTooltip]="itemCard.description" tooltipPosition="right"
              >{{ itemCard.description }}</p>
            </p-card>
          }
        } @else if (stateRequest.loading) {
          <div class="flex gap-4 w-full justify-content-center flex-wrap">
            <p-skeleton width="20rem" height="15rem"/>
            <p-skeleton width="20rem" height="15rem"/>
            <p-skeleton width="20rem" height="15rem"/>
          </div>
        } @else if (stateRequest.error) {
          <div class="flex flex-column align-items-center justify-content-center w-full p-6">
            <i class="pi pi-exclamation-triangle text-4xl text-orange-500 mb-3"></i>
            <h3
              class="text-gray-900 font-principal mb-2">{{ 'components.app-sede-electronica-noticias.error-titulo' | translate }}</h3>
            <p
              class="text-gray font-principal text-center mb-4">{{ 'components.app-sede-electronica-noticias.error-descripcion' | translate }}</p>
            <button pButton
                    class="p-button-outlined p-button-sm"
                    (click)="getNoticias()">
              <i class="pi pi-refresh mr-2"></i>
              {{ 'components.app-sede-electronica-noticias.intentar-nuevamente' | translate }}
            </button>
          </div>
        }
        <div class="flex justify-content-center mt-5 w-full flex-column sm:flex-row gap-3">
          <button pButton class="p-button-rounded noticia-button w-full
               sm:w-auto border-outline-blue-light  sm:hidden block
      bg-button-outline-blue-light text-blue-general flex justify-content-center" (click)="verMasNoticias(false)">
            <span>{{ 'components.app-sede-electronica-noticias.ver-mas-noticias' | translate }}</span>
            <i class="pi pi-angle-right ml-3"></i>
          </button>
          <button pButton (click)="verMasNoticias(true)" class="p-button-rounded w-full sm:w-auto
       noticia-button gradient-button-blue flex justify-content-center">
            <span>{{ textButton() }}</span>
            <i class="pi pi-angle-right ml-3"></i>
          </button>
        </div>
      </section>
    </section>
  `,
  styleUrl: './sede-electronica-noticias.component.scss'
})
export class SedeElectronicaNoticiasComponent implements OnInit {

  private readonly noticiaService: NoticiasRssService = inject(NoticiasRssService);
  private readonly _t: TranslateService = inject(TranslateService);
  private platformId = inject(PLATFORM_ID);
  textButton: WritableSignal<string> = signal('')
  cards: CardNoticias[] = [];
  translation: any = {};
  stateRequest: StateRequest = new StateRequest();

  ngOnInit(): void {
    this.getTranslate()
      .then(() => this.detectarDispositivoMovil());
    this.getNoticias();
  }

  getNoticias() {
    this.stateRequest.loading = true;
    this.noticiaService.getAllNoticias().subscribe({
      next: noticias => {
        this.stateRequest.loading = false;
        this.cards = noticias;
      }, complete: () => this.stateRequest.complete = true,
      error: () => {
        this.stateRequest.loading = false;
        this.stateRequest.error = true;
      }
    });
  }

  private async getTranslate(): Promise<void> {
    this.translation = await firstValueFrom(forkJoin({
      irPortalDesktop: this._t.get('components.app-sede-electronica-noticias.ir-al-portal-desktop'),
      irPortal: this._t.get('components.app-sede-electronica-noticias.ir-al-portal'),
    }));
  }

  verMasNoticias(redirect: boolean, link?: string): void {
    const anchor = document.createElement('a');
    anchor.rel = 'noopener';
    let route = undefined;
    if (!redirect) {
      anchor.target = '_blank';
      route = link ? link : this.getUrl('noticia');
    }
    anchor.href = route ?? this.getUrl('portal');
    anchor.click();
  }

  private getUrl(type: string): string {
    if (type == 'noticia') {
      return 'https://www.gobernaciondelmagdalena.gov.co/noticias-2';
    }
    if (type == 'portal') {
      return 'https://www.gobernaciondelmagdalena.gov.co/';
    }
    return '';
  }

  private detectarDispositivoMovil(): void {
    if (isPlatformBrowser(this.platformId)) {
      const value = (this.isMobile()) ? this.translation.irPortal : this.translation.irPortalDesktop;
      this.textButton.set(value);
    }
  }

  private isMobile(): boolean {
    return window.innerWidth <= 768;
  }
}

